import React, {useCallback, useMemo, useState} from 'react';
import {Box, CircularProgress, Dialog, Paper, TableCell, Tooltip} from '@mui/material';
import MasterTable from 'ui-component/master-table/MasterTable';
import Loader from 'ui-component/Loader';
import MainCard from 'ui-component/cards/MainCard';
import {HeadCell} from 'types';
import {endOfYear, format, parseJSON, startOfYear, subYears} from 'date-fns';
import {RangeInput} from '@mui/lab/DateRangePicker/RangeTypes';
import {buildDebts, CartesetDataHolder} from '../../../../types/CartesetDataHolder';
import {IconArchive, IconCurrencyShekel, IconPhone} from '@tabler/icons';
import {no_padding, number_style} from '../../../../styles/tablecell';
import {useDispatch, useSelector} from 'react-redux';
import {RootState} from '../../../../store';
import {fillCustomers, getSpecificCustomer, selectCustomerById} from '../../../../store/customers/customersSlice';
import {CartesetFilterBar} from './CartesetFilterBar';
import DocumentPreview from '../documents/DocumentPreview';
import {
    CUSTOMER_DOCUMENTS, refreshDocs,
    useGetCustomerAllDocuments, useGetSupplierAllDocuments,
    usePaidDvs,
    usePaidTN
} from '../../../../hooks/customer/documents.hooks';
import {documentStatuses, displayDocumentId, ExtendedDocument, supplierInvoiceNumberOf} from '../../../../types/DocumentsTypes/Document';
import {deserializeCarteset, deserializeDocument} from '../../../../types/DocumentsTypes/utils';
import config from '../../../../config';
import {enUS, he} from 'date-fns/locale';
import {MasterTableHeaderToolbarProps} from 'ui-component/master-table/MasterTableColumnHeader';
import {useQueryClient} from 'react-query';
import Button from '@mui/material/Button';
import DialogAddManualDate from '../../../../ui-component/dialogs/addManualDebt';
import Avatar from "../../../../ui-component/extended/Avatar";
import GenerativeLoader, {
    GenerativeLoaderChip, GenerativeLoaderChipForPrint,
    GenerativeLoaderChipSum
} from "../../../../ui-component/GenerativeLoader";
import JSDB from "../../../../fromKotlin/nk";
import {getFile, pricesPdf} from "../../../../api/customer/files.api";
import {
    buildClientCartesetDebt,
    buildNotesSum,
    buildSupNotesSum, calculateOptimized, clearCarteset,
    createDvsTaxNoteSplitted,
    getCartesetPDF, getDocumentPhoto, getMassDocumentPdf, getNameOfDoc,
    sendPdfEmail, update_all_tn
} from "../../../../api/customer/documents.api";
import {Email, Share} from "@mui/icons-material";
import xslx, {email, pdf, whatsapp2} from "../../../../assets/images";
import AgTable, {getSelectedRows, ITableControls, selectionMode} from 'ui-component/ag-grid/Ag-Table';
import AddIcon from '@mui/icons-material/Add';
import {numberPinnedRowRenderer, rowGroupRenderer} from 'ui-component/ag-grid/ag-table-utils';
import MyChip from "../../../../ui-component/extended/Chip";
import {FormattedMessage} from "react-intl";
import {AllTaxNoteHolder} from "../../../../types/AllTaxNoteHolder";
import Swal from "sweetalert2";
import {SNACKBAR_OPEN} from "../../../../store/actions";
import DoNotDisturbOnOutlinedIcon from "@mui/icons-material/DoNotDisturbOnOutlined";
import FunctionsOutlinedIcon from "@mui/icons-material/FunctionsOutlined";
import PrintIcon from "@mui/icons-material/PrintTwoTone";
import ShowImageDialog from "../../../../ui-component/dialogs/showImageDialog";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import {dateObjectToString, dateObjectToStringIsrael, roundDecimals} from "../../../../fromKotlin/nkutils";
import {IAgChip} from 'ui-component/ag-grid/components/Ag-Chip';
import basicPinnedControl from "../../../../ui-component/ag-grid/utils/basicPinnedTopRow";
import SendMessageDialog from "../../../../ui-component/dialogs/sendMessage";
import DocumentNew from "../documents/DocumentNew";
import {cloneDeep} from "lodash";
import {getSpecificSupplier, selectSupplierById} from "../../../../store/suppliers/suppliersSlice";
import {changeAllCustomers} from "../../../../api/customer";
import {getClientDebt} from "../../../../api/customer/analytics.api";
import LinkIcon from "@mui/icons-material/Link";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import {buildCartesetLinks} from "./cartesetLinks";
import CartesetLinksOverlay from "./CartesetLinksOverlay";
import PaymentLinkDialog from 'ui-component/PaymentLinkDialog';
import {getTranzilaConfig} from '../../../../api/customer/tranzila.api';
import {
    collectPayableFromCartesetRows,
    groupPayableByClient,
    paymentSendPayload,
} from '../../../../utils/paymentLinkDocs';

const SHOW_LINKS_KEY = 'carteset_show_links';
const cartesetRowKey = (row: any) => `${row?.name}_${row?.id}`;

const cartesetColumns: HeadCell[] = [
    {
        id: 'checkbox',
        numeric: false,
        label: '',
        align: 'center',
        type: 'string',
    },
    {
        id: 'name',
        numeric: true,
        label: 'סוג מסמך',
        align: 'center',
        type: 'string',
        filterable: true,
        sortable: true,
        enableRowGroup: true
    },
    {
        id: 'id',
        numeric: false,
        label: 'אסמכתא',
        align: 'center',
        type: 'string',
        filterable: true,
        extractFunc: (row) => displayDocumentId(row.doc ?? row),
        searchFunc: (row) => {
            const src = row.doc ?? row;
            const shown = displayDocumentId(src);
            const internal = src?.id != null && src.id !== 0 ? String(src.id) : '';
            const inv = supplierInvoiceNumberOf(src);
            return [...new Set([shown, internal, inv].filter((s) => s))];
        },
        cellRenderer: ({params, setCurrentDocument, setPreviewOpen}) => {
            return (
                <div style={{
                    cursor: "pointer",
                    color: "blue",
                }} onClick={() => {
                    setCurrentDocument(params?.node?.data?.doc);
                    setPreviewOpen(true);
                }}>
                    {params?.value}
                </div>
            )
        },
    },
    {
        id: 'supplierInvoiceNumber',
        numeric: false,
        label: 'מספר חשבונית ספק',
        align: 'center',
        type: 'string',
        cellClass: 'stringType',
        filterable: true,
        hide: true,
        extractFunc: (row) => {
            const doc = row?.doc ?? row;
            return supplierInvoiceNumberOf(doc);
        },
    },
    {
        id: 'agent',
        numeric: true,
        label: 'סוכן',
        align: 'center',
        type: 'number',
        filterable: true,
        hide: true,
        enableRowGroup: true
    },
    {id: 'driver', numeric: true, label: 'נהג', align: 'center', type: 'number', filterable: true, hide: true},
    {
        id: 'cname',
        numeric: true,
        label: 'שם',
        align: 'center',
        type: 'string',
        filterable: true,
        sortable: true,
        enableRowGroup: true
    },
    {
        id: 'comment',
        numeric: true,
        label: 'הערות',
        align: 'center',
        type: 'string',
        filterable: true,
        sortable: true,
        hide: true
    },
    {
        id: 'date', numeric: false, label: 'תאריך מסמך', align: 'center', filterable: true, enableRowGroup: true,
        searchFunc: (row) => {
            const v = row.date.getFullYear() == 2011 ? 'ללא תאריך' : format(row.date, 'PPP', {locale: config.i18n === 'he' ? he : enUS})
            const z = row.date.getFullYear() == 2011 ? 'ללא תאריך' : JSDB().getDatesManipulator().dateIsrael((dateObjectToStringIsrael(row.date)));
            return [v, z];
        },
        extractFunc: (row) => {
            if (!row?.date) return '';
            return row?.date?.getFullYear() == 2011 ? 'ללא תאריך' : ((dateObjectToStringIsrael(row?.date)));
        }
    },
    {
        id: 'status', numeric: false, label: 'סטטוס', align: 'center', type: 'string', filterable: false,
        extractFunc: (row) => {
            return documentStatuses[row?.status];
        },
        cellRenderer({params}) {
            if (params?.node?.group) return params.value || '';
            return (params?.data?.status ?
                    <MyChip
                        label={documentStatuses[params?.data?.status]}
                        variant='outlined'
                        size='small'
                        chipcolor={
                            // eslint-disable-next-line no-nested-ternary
                            params?.data?.status === 'pending_approval'
                                ? 'warning'
                                // eslint-disable-next-line no-nested-ternary
                                : params?.data?.status === 'approved'
                                    ? 'success'
                                    : params?.data?.status === 'open' || params?.data?.status === 'partly_closed'
                                        ? 'error'
                                        : ''
                        }
                    /> : <></>
            );
        },
    },
    {
        id: "open_tn",
        numeric: false,
        label: 'סכום חשבונית פתוחה',
        align: 'center',
        type: 'string',
        filterable: false,
        pinnedRowProps: {
            action: "sum",
            format: "currency"
        },
        cellRenderer({params}) {
            if (params?.node?.rowPinned) return params.value || "";
            if (params?.node?.group) return rowGroupRenderer(params?.data?.open_tn);
            return (
                <div style={{textAlign: "right", direction: "ltr",color:params?.data?.open_tn??0>0 ? 'red':undefined}}>
                    ₪ {params?.data?.open_tn?.toLocaleString()}
                </div>
            );
        },
        editable: true,
    },
    {
        id: 'value_tn',
        numeric: true,
        label: 'חובה',
        align: 'center',
        type: 'number',
        filterable: false,
        pinnedRowProps: {
            action: "sum",
            format: "currency"
        },
        moneyFunc: (d: any[]) => d.reduce((sum, x) => sum + x.value_tn, 0),
        cellRenderer({params}) {
            if (params?.node?.rowPinned) return params.value || "";
            return (
                <div style={{textAlign: "right", direction: "ltr"}}>
                    ₪ {params?.value?.toLocaleString()}
                </div>
            );
        },
    },
    {
        id: 'value_pay',
        numeric: true,
        label: 'זכות',
        align: 'center',
        type: 'number',
        filterable: false,
        pinnedRowProps: {
            action: "sum",
            format: "currency"
        },
        moneyFunc: (d: any[]) => d.reduce((sum, x) => sum + x.value_pay, 0),
        cellRenderer({params}) {
            if (params?.node?.rowPinned) return params.value || "";
            return (
                <div style={{textAlign: "right", direction: "ltr"}}>
                    ₪ {params?.value?.toLocaleString()}
                </div>
            );
        },
    },
    {
        id: 'debt',
        numeric: false,
        label: 'יתרה',
        align: 'center',
        type: 'string',
        filterable: false,
        pinnedRowProps: {
            action: "sum",
            format: "currency"
        },
        cellRenderer({params, totalDebt}) {
            if (params?.node?.group) return rowGroupRenderer(params?.data?.debt);
            if (params?.node?.rowPinned) return rowGroupRenderer(totalDebt) || "";

            if (params?.data?.debt === 0) {
                return (
                    <div style={{
                        textAlign: "right",
                        direction: "ltr",
                        backgroundColor: "#00800080",
                        fontWeight: "bold"
                    }}>
                        ₪ {params?.data?.debt?.toLocaleString()}
                    </div>
                );
            }
            return (
                <div style={{textAlign: "right", direction: "ltr"}}>
                    ₪ {params?.data?.debt?.toLocaleString()}
                </div>
            );
        },
    },
    {
        id: 'days_to_pay',
        numeric: false,
        label: 'תאריך לתשלום', hide: true,
        align: 'center',
        type: 'dateStr', filterable: true,

        extractFunc: (row) => {
            if (row.type != 'invoice')
                return ''
            const client = JSDB().getClient(Number(row?.entId))?.first;
            const daysToPay = client?.days_to_pay ?? 0;
            const curD = dateObjectToString(row?.date);

            let invoiceDate = new Date(curD);
            let dueDate = new Date(invoiceDate.getFullYear(), invoiceDate.getMonth() + 1, 0); // End of invoice month

// Add months and residue days
            dueDate.setMonth(dueDate.getMonth() + Math.floor(daysToPay / 30));
            dueDate.setDate(dueDate.getDate() + (daysToPay % 30));

// Format and return
            return JSDB().getDatesManipulator().dateIsrael(dateObjectToString(dueDate));

        },
    },
    {
        id: 'comment',
        numeric: false,
        label: 'הערות', hide: true,
        align: 'center',
        filterable: true,
        editable: true,

    }


];

export const MultiDocumentsActions = React.memo(({
                                                     closeDVS,
                                                     selected,
                                                     range,
                                                     sup
                                                 }: { closeDVS: any, selected: CartesetDataHolder[], range: RangeInput<null> ,sup?:boolean}) => {
        const [loader, setLoader] = useState(false);
        return <div className='document-actions-div' style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            marginTop: 12,
        }}>
            <Tooltip title='סגירת חשבוניות ידנית' onClick={(a) => {
                Swal.fire({
                    title: 'האם תרצה לסגור חשבוניות ידנית?',
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonText: 'סגור חשבוניות',
                    cancelButtonText: 'בטל'
                }).then((result) => {
                    if (result.isConfirmed) {
                        /* Perform insert, update, and delete to the database here */
                        closeDVS.mutate(selected.map((x) => x.doc));
                        // callbackNetworkResult(()=>close_all_dv(selected),(result)=>{})
                    }
                });

            }}>
                <MyChip icon={<DoNotDisturbOnOutlinedIcon/>} sx={{
                    ml: 0.5, mr: 0.5, '& .MuiChip-icon': {
                        color: '#000080',
                    },
                }} label="סגירת חשבוניות" variant="outlined"/>

            </Tooltip>
            <Tooltip title={'יצא מסמכים pdf'}>

                <GenerativeLoaderChip func={(setLoader) => {
                    Swal.fire({
                        title: 'מקור/העתק',
                        icon: 'warning',
                        showCancelButton: true,
                        confirmButtonText: 'מקור',
                        cancelButtonText: 'ביטול',
                        denyButtonText: 'העתק',
                        showDenyButton: true
                    }).then((result) => {
                        const docs = selected.map((d) => d.doc);
                        if (result.isConfirmed) {

                            if (selected.some((d) => d.type == 'supplier_note')) {
                                getFile(false, () => buildSupNotesSum(docs?.filter((d) => d.type == 'supplier_note'), undefined), getNameOfDoc(docs), 'application/pdf', undefined, setLoader);
                            } else {
                                getFile(false, () => getMassDocumentPdf(docs, undefined, undefined, true), getNameOfDoc(docs), 'application/pdf', undefined, setLoader);
                            }
                        } else if (result.isDenied) {
                            if (selected.some((d) => d.type == 'supplier_note')) {
                                getFile(false, () => buildSupNotesSum(docs?.filter((d) => d.type == 'supplier_note'), undefined), getNameOfDoc(docs), 'application/pdf', undefined, setLoader);
                            } else {
                                getFile(false, () => getMassDocumentPdf(docs, undefined, undefined, false), getNameOfDoc(docs), 'application/pdf', undefined, setLoader);
                            }
                        }
                    });


                }} icon={<Avatar variant='square' sx={{cursor: 'pointer', bgcolor: 'white', width: '24px', height: '24px'}}
                                 src={pdf}/>}/>

            </Tooltip>
            <Tooltip title='שליחה ישירה למייל' onClick={() => {

                selected?.forEach((x) => {
                    getFile(false, () => getCartesetPDF(parseJSON(range[0]!), parseJSON(range[1]!), Number(x?.doc?.entId), undefined, sup), `כרטסת`, undefined, undefined, setLoader, async (b, t) => {
                        const c = sup
                            ? JSDB().getSupplier(Number(x?.doc?.entId)).first
                            : JSDB().getClient(Number(x?.doc?.entId)).first;
                        if (c?.getEmail?.() != '') {
                            const sent = sendPdfEmail(b, c.getEmail(), t);
                            setLoader(false);
                            return sent;
                        } else {
                            setLoader(false);
                            return false;
                        }
                    });

                });
            }
            }>
                <MyChip icon={loader ? <CircularProgress size={20}
                /> : <Email/>} sx={{
                    ml: 0.5, mr: 0.5, '& .MuiChip-icon': {
                        color: '#000080',
                    },
                }} label="שליחה ישירה למייל" variant="outlined"/>
            </Tooltip>
        </div>
    }
);

const CartesetList = ({
                          cid,
                          rowsPerPage = 100,
                          withSelection = false,
                          selectionOptions,
                          selectedInit,
                          sup

                      }: {
    cid: string;
    rowsPerPage?: number;
    withSelection?: boolean;
    selectionOptions?: MasterTableHeaderToolbarProps<CartesetDataHolder>;
    selectedInit?: ExtendedDocument[],
    sup?: boolean
}) => {
    const dispatch = useDispatch();
    const [isMutating, setIsMutating] = React.useState(false);
    const [range, setRange] = React.useState<RangeInput<null>>([subYears(startOfYear(new Date()), 1), endOfYear(new Date())]);
    const [cols, setCols] = React.useState<HeadCell[]>(cartesetColumns);
    const customer = sup ? useSelector((state: RootState) => selectSupplierById(state, cid)) : useSelector((state: RootState) => selectCustomerById(state, cid));
    const cache = useQueryClient();
    const pool = sup ? "SUP_DOCUMENTS_FOR_DEBT" : "CUSTOMER_DOCUMENTS_FOR_DEBT"
    React.useEffect(() => {
        cache.removeQueries(pool);
        setDebt(0)
    }, [dispatch]);
    React.useEffect(() => {
        const newCols = cloneDeep(cartesetColumns)

        // Supplier carteset: invoices are credit (זכות), payments are debit (חובה)
        if (sup) {
            const hova = newCols.find((c) => c.id === 'value_tn' && c.label === 'חובה');
            const zchut = newCols.find((c) => c.id === 'value_pay' && c.label === 'זכות');
            if (hova && zchut) {
                hova.extractFunc = (row) => row.value_pay;
                hova.moneyFunc = (d: any[]) => d.reduce((sum, x) => sum + x.value_pay, 0);
                zchut.extractFunc = (row) => row.value_tn;
                zchut.moneyFunc = (d: any[]) => d.reduce((sum, x) => sum + x.value_tn, 0);
            }
        }

        if (JSDB().getUser().first?.add_carteset === 5) {
            const openCol = newCols.find((c) => c.id === 'open_tn');
            if (openCol) openCol.hide = true;
            const commentCols = newCols.filter((c) => c.id === 'comment');
            commentCols.forEach((col, i) => {
                col.hide = i !== 0;
                if (i === 0) {
                    col.extractFunc = (row) =>
                        String(row?.comment ?? '').replace(/\s+/g, ' ').trim().slice(0, 12);
                }
            });
        }

        const notes5 = JSDB().getUser().first?.getJsonDesc("notes5")
        if (notes5) {
            newCols.push({
                id: "notes5",
                label: notes5.name,
                numeric: false,
                hide: true,
                priority: -1,
                extractFunc: (row) => {
                    return row?.doc?.notes5
                }
            },)

        } else {
            newCols.push({
                id: "notes5",
                label: "הערות 5",
                numeric: false,
                hide: true,
                priority: -1,
                extractFunc: (row) => {
                    return row?.doc?.notes5
                }
            })

        }
        setCols(newCols)


    }, [sup]);
    const {data: docs, isLoading, refetch} = sup ? useGetSupplierAllDocuments(parseJSON(range[0]!),
        parseJSON(range[1]!),
        true,
        cid, true, pool) : useGetCustomerAllDocuments(
        parseJSON(range[0]!),
        parseJSON(range[1]!),
        true,
        cid,
        true,
        true,
        undefined,
        undefined,
        pool
    );

    const [carteset, setCarteset] = React.useState<CartesetDataHolder[]>([]);
    const [addOpen, setAddOpen] = React.useState<boolean>(false);
    const [debt, setDebt] = React.useState<number>(0);
    const [recipt, setRecipt] = React.useState<any>(undefined);
    const [send, setSend] = React.useState(false);
    const gridRef = React.useRef<any>(null);
    const [showLinks, setShowLinks] = React.useState<boolean>(
        () => localStorage.getItem(SHOW_LINKS_KEY) !== '0'
    );
    const toggleLinks = useCallback(() => {
        setShowLinks((prev) => {
            localStorage.setItem(SHOW_LINKS_KEY, prev ? '0' : '1');
            return !prev;
        });
    }, []);
    const cartesetLinks = useMemo(
        () => (showLinks ? buildCartesetLinks(carteset) : []),
        [carteset, showLinks]
    );
    React.useEffect(() => {
        if (!customer) {
            if (sup) dispatch(getSpecificSupplier(Number(cid)));
            else dispatch(getSpecificCustomer(Number(cid)));
        }
    }, [dispatch, sup, cid, customer]);
    // fetch events data


    React.useEffect(() => {
        if (!isLoading) {
            cache.removeQueries(pool);
            refetch();
        }
    }, [range, dispatch]);

    React.useEffect(() => {
        console.log("docs", docs)
        if (docs) {
            const data = buildDebts(docs.filter((d) => (d.type == 'receipt' || d.type == 'invoice' || d.type == 'tax_invoice' || d.type == 'refund' || d.type == 'debt' || d.type == 'sup_invoice' || d.type == 'sup_tax_invoice' || d.type === 'sup_refund' || d.type === 'sup_receipt' || d.type == 'sup_debt')).map((t) => deserializeCarteset(t)), parseJSON(range[0]!))
            if(sup){
                if (data.length > 0) {
                    setDebt(data[0].debt)
                }
                setCarteset(data);
            } else {

                calculateOptimized((dateObjectToString(parseJSON(range[0]!))), ((parseJSON(range[1]!))),Number(customer?.nkObject?.masterBranch?.id ?? cid)).then((d )=>setDebt(roundDecimals(d)))
            }




            setCarteset(data);
        }
    }, [docs]);
    const clearCartesetVoid = useCallback(() => {
        if (debt != 0 && !sup) {
            clearCarteset(Number(cid), new Date(),sup).then((res) => {
                if (res) {
                    cache.invalidateQueries(pool);
                    refetch();
                }
            })

        }
    }, [carteset, cid,sup]);
    const [previewOpen, setPreviewOpen] = React.useState<boolean>(false);
    const [currentDocument, setCurrentDocument] = React.useState<ExtendedDocument | undefined>(undefined);
    const closeTNS = usePaidTN(pool,sup,refetch);
    const refresh = refreshDocs(pool)
    const chipDetails = useMemo(() => {
        return [
            {
                label: () => "אפס כרטסת",
                action: () => Math.abs(debt) < 100 && debt != 0 ? Swal.fire({
                    title: 'האם תרצה לאפס כרטסת?',
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonText: 'אפס כרטסת',
                    cancelButtonText: 'בטל'
                }).then((result) => {
                    if (result.isConfirmed) {
                        /* Perform insert, update, and delete to the database here */
                        clearCartesetVoid()
                    }
                }) : undefined,
                icon: <IconArchive/>,
                tooltip: "אפס כרטסת",
                active: () => Math.abs(debt) < 100 && debt != 0,
            },
            {
                label: () => (showLinks ? "בלי חיבורים" : "הצג חיבורים"),
                action: toggleLinks,
                icon: showLinks ? <LinkOffIcon/> : <LinkIcon/>,
                tooltip: showLinks
                    ? "הסתר את החיבורים בין קבלות לחשבוניות שהן סוגרות"
                    : "הצג חיבורים מקבלות (זכות) לחשבוניות (חובה) שהן סוגרות",
                active: () => !showLinks,
            },
            {
                label: () => "הוסף חוב ידני",
                action: () => setAddOpen(true),
                icon: <AddIcon/>,
                tooltip: "הוסף חוב ידני",
                active: () => addOpen,
            },
            {
                action: (params, setLoader) => {
                    if(sup){
                        getFile(false, () => getCartesetPDF(parseJSON(range[0]!), parseJSON(range[1]!), cid, true, true), 'כרטסת', undefined, undefined, setLoader);

                    } else {
                        getFile(false, () => getCartesetPDF(parseJSON(range[0]!), parseJSON(range[1]!), cid,true), 'כרטסת', undefined, undefined, setLoader);
                    }

                },
                label: () => "חשבוניות פתוחות לPDF",
                icon: <Avatar variant='square'
                              sx={{cursor: 'pointer', bgcolor: 'white', width: '24px', height: '24px'}}
                              src={pdf}/>,
                tooltip: 'יצא לPDF',
                active: () => false,
                withLoader: true
            },
            {
                action: (params, setLoader) => {
                    if(sup){
                        getFile(false, () => getCartesetPDF(parseJSON(range[0]!), parseJSON(range[1]!), cid, undefined, true), 'כרטסת', undefined, undefined, setLoader);

                    } else {
                        getFile(false, () => getCartesetPDF(parseJSON(range[0]!), parseJSON(range[1]!), cid), 'כרטסת', undefined, undefined, setLoader);
                    }

                },
                label: () => "יצא לPDF",
                icon: <Avatar variant='square'
                              sx={{cursor: 'pointer', bgcolor: 'white', width: '24px', height: '24px'}}
                              src={pdf}/>,
                tooltip: 'יצא לPDF',
                active: () => false,
                withLoader: true
            }, {
                action: (params, setLoader) => {
                    setSelectedCustomersSend({
                        selected: [Number(cid)],
                        defaultPlatform: 'whatsapp',
                        files: {
                            ids: [Number(cid)],
                            types: [sup? 22:9],
                        },
                        msg: '#docs',
                        subject: 'שליחת מסמכים',
                    })
                },
                label: () => "שלח ווטסאפ/ אימייל",
                icon: <Share color={send ? 'success' : 'error'}
                             sx={{width: '24px', height: '24px'}}/>,
                tooltip: 'יצא לPDF',
                active: () => send,
                withLoader: true
            },
        ]
    }, [addOpen, send, range, cid, debt, showLinks, toggleLinks]);

    const selectionControls = useCallback((selectedItems) => {
        return <MultiDocumentsActions
            closeDVS={closeTNS}
            selected={selectedItems}
            range={range}
        />
    }, [closeTNS]);

    const hasTranzilaConfig = React.useMemo(() => !!getTranzilaConfig(), []);

    const contextMenu = useCallback((params) => {
        const hideContextMenu = () => {
            params?.api?.hidePopupMenu?.();
        };
        const ranges = params?.api?.getCellRanges();
        let nodesInRange: any = [];
        const nodesInRangeSet: any = new Set();
        const selected = getSelectedRows(params?.api)

        selected?.forEach((row) => {

            if (row && !nodesInRangeSet.has(row)) {
                nodesInRangeSet.add(row);
                nodesInRange.push(row);
            }
        })
        if (!selected.length) {
            ranges?.forEach((range) => {

                const startRowIndex = range?.startRow.rowIndex < range?.endRow.rowIndex ? range?.startRow.rowIndex : range?.endRow.rowIndex;
                const endRowIndex = range?.endRow.rowIndex > range?.startRow.rowIndex ? range?.endRow.rowIndex : range?.startRow.rowIndex;
                for (let i = startRowIndex; i <= endRowIndex; i++) {
                    const row = params.api?.getDisplayedRowAtIndex(i);

                    if (row?.allLeafChildren) {
                        row?.allLeafChildren?.forEach((child) => {
                            if (!nodesInRangeSet.has(child?.data)) {
                                nodesInRangeSet.add(child?.data);
                                nodesInRange.push(child?.data);
                            }
                        })
                    } else {
                        if (!nodesInRangeSet.has(row?.data)) {
                            nodesInRangeSet.add(row?.data);
                            nodesInRange.push(row?.data);
                        }
                    }


                }
            });
        }
        const contextItems: {
            name: string;
            action: (params: any) => void;
        }[] | {
            name: string;
            subMenu: {
                name: string;
                action: (params: any) => void;
            }[];
        }[] = [
            {
                name: "פעולות לחשבוניות",
                subMenu: [
                    {
                        name: "הנפק קבלה מהחשבוניות הפתוחות המסומנות",
                        action() {

                            const goodNodes = nodesInRange.filter((n) => n.doc && (n.status == 'open' || n.status=='partly_closed') && (n.doc.type == 'invoice' || n.doc.type == 'refund')).map((n) => n.doc)
                            if (goodNodes.length >= 1) {
                                setRecipt({
                                    documents: goodNodes,
                                    action: "create",
                                    type: "receipt",
                                })
                            }

                        },
                    },
                    ...(!sup && hasTranzilaConfig ? [{
                        name: "תשלום בלינק",
                        icon: `<img src="${whatsapp2}" style="width: 20px; height: 20px; margin-right: 5px;"/>`,
                        action() {
                            const groups = groupPayableByClient(
                                collectPayableFromCartesetRows(nodesInRange, Number(cid)),
                            );
                            hideContextMenu();
                            if (!groups.length) {
                                Swal.fire({
                                    icon: 'info',
                                    title: 'אין יתרה פתוחה',
                                    text: 'סמן חשבוניות פתוחות (או חלקיות) ואז שלח בלינק.',
                                });
                                return;
                            }
                            setSelectedCustomersSend(paymentSendPayload(groups));
                        },
                    }, {
                        name: "צור קישור תשלום לחשבוניות הפתוחות",
                        action() {
                            const good = nodesInRange.filter((n) =>
                                n.doc
                                && (n.status === 'open' || n.status === 'partly_closed')
                                && (n.doc.type === 'invoice' || n.type === 'invoice')
                            );
                            const sum = roundDecimals(
                                good.reduce((s, n) => s + Math.max(Number(n.open_tn) || 0, 0), 0),
                                2,
                            );
                            if (!(sum > 0)) {
                                Swal.fire({
                                    icon: 'info',
                                    title: 'אין יתרה פתוחה',
                                    text: 'סמן חשבוניות פתוחות (או חלקיות) ואז צור קישור תשלום.',
                                });
                                return;
                            }
                            setPaymentLinkOpen({
                                amount: sum,
                                invoiceIds: good
                                    .map((n) => Number(n.doc?.id))
                                    .filter((invoiceId) => Number.isFinite(invoiceId) && invoiceId > 0),
                            });
                        },
                    }] : []),
                    {
                        name: "סגירת חשבוניות",
                        action: () => {
                            Swal.fire({
                                title: 'האם תרצה לסגור חשבוניות ידנית?',
                                icon: 'warning',
                                showCancelButton: true,
                                confirmButtonText: 'סגור חשבוניות',
                                cancelButtonText: 'בטל'
                            }).then((result) => {
                                if (result.isConfirmed) {
                                    /* Perform insert, update, and delete to the database here */
                                    closeTNS.mutate(nodesInRange.map((x) => x.doc));
                                    // callbackNetworkResult(()=>close_all_dv(selected),(result)=>{})
                                }
                            });
                        }
                    },
                    {
                        name: "פתיחת חשבוניות",
                        action: () => {
                            Swal.fire({
                                title: 'האם תרצה לפתוח את החשבוניות?',
                                icon: 'warning',
                                showCancelButton: true,
                                confirmButtonText: 'פתח חשבוניות',
                                cancelButtonText: 'בטל'
                            }).then(async (result) => {
                                if (!result.isConfirmed) return;

                                const isClientTn = (t: string) =>
                                    t === 'invoice' || t === 'refund';
                                const isSupTn = (t: string) =>
                                    t === 'sup_invoice' || t === 'sup_refund';

                                const eligible = nodesInRange.filter(
                                    (n) =>
                                        n.doc &&
                                        n.status === 'closed' &&
                                        (sup ? isSupTn(n.type) : isClientTn(n.type))
                                );

                                if (eligible.length === 0) {
                                    await Swal.fire({
                                        title: 'לא נמצאו חשבוניות סגורות בבחירה',
                                        text: 'בחר שורות של חשבוניות/זיכויים במצב סגור.',
                                        icon: 'info',
                                    });
                                    return;
                                }

                                const payload = eligible.map((n) => ({
                                    ...n,
                                    open_tn: n.value_tn,
                                    status: 'open' as const,
                                }));

                                try {
                                    await update_all_tn(
                                        payload.filter((x) =>
                                            sup ? isSupTn(x.type) : isClientTn(x.type)
                                        ),
                                        sup
                                    );
                                    await cache.invalidateQueries(pool);
                                    refetch();
                                    dispatch({
                                        type: SNACKBAR_OPEN,
                                        open: true,
                                        anchorOrigin: {
                                            vertical: 'bottom',
                                            horizontal: 'left',
                                        },
                                        message:
                                            eligible.length === 1
                                                ? 'החשבונית נפתחה'
                                                : `${eligible.length} חשבוניות נפתחו`,
                                        variant: 'alert',
                                        alertSeverity: 'success',
                                    });
                                } catch (e) {
                                    console.error(e);
                                    await Swal.fire({
                                        title: 'שגיאה בפתיחת חשבוניות',
                                        icon: 'error',
                                    });
                                }
                            });
                        }
                    },


                ],
            }, {
                name: "שלח מסמכים למייל",
                icon: `<img src="${email}" style="width: 20px; height: 20px; margin-right: 5px;"/>`,
                action() {
                    const lst = [...nodesInRange]
                    setSelectedCustomersSend({
                        selected: lst.map((u) => Number(u.doc.entId)),
                        defaultPlatform: 'email',
                        files: {
                            ids: lst.map((u) => Number(u.doc.id)),
                            types: lst.map((u) => u.doc.nkObject.getConnectedDocType()),
                        },
                        msg: 'מסמכים',
                        subject: 'שליחת מסמכים',


                    })

                    hideContextMenu();
                },
            },

            {
                name: "שלח תעודות לווטסאפ",
                icon: `<img src="${whatsapp2}" style="width: 20px; height: 20px; margin-right: 5px;"/>`,
                action() {
                    const lst = [...nodesInRange]
                    setSelectedCustomersSend({
                        selected: lst.map((u) => Number(u.doc.entId)),
                        defaultPlatform: 'whatsapp',
                        files: {
                            ids: lst.map((u) => Number(u.doc.id)),
                            types: lst.map((u) => u.doc.nkObject.getConnectedDocType()),
                        },
                        msg: 'מסמכים',
                        subject: 'שליחת מסמכים',
                    })

                    hideContextMenu();
                },
            },
        ];

        return contextItems;
    }, [closeTNS, cache, pool, refetch, sup, dispatch, hasTranzilaConfig]);

    const tableControls: ITableControls = {
        tableName: "costumers/costumers-list/carteset-list",
        sidebar: true,
        chipDisplayAmount: 5,
        groupBy: {
            enable: true,
            rowGroup: true,
            pivot: true,
            value: true,
            allowSave: true,
        },
        selection: {
            enable: true,
            mode: "multiple" as selectionMode,
        },

        extractId: (row) => `${row.data.name}_${row.data.id}`,

    }

    const cellRenderer = React.useCallback(
        (params, column) => {

            if (column.cellRenderer) {
                return column.cellRenderer({
                    params,
                    setPreviewOpen,
                    setCurrentDocument,
                    totalDebt: debt
                });
            }
            if (typeof params?.value === "object") {
                const value = params?.value?.value || "";
                return value;
            }
            return params.value || "";
        },
        [debt]
    );
    const saveDataCallback = React.useCallback(async (data) => {
        try {
            const eligible = data
                .filter((x) =>
                    sup
                        ? (x.type === 'sup_invoice' || x.type === 'sup_refund')
                        : (x.type === 'invoice' || x.type === 'refund')
                )
                .map((x) => {
                    const openLeft = Math.abs(Number(x.open_tn))
                    // Zeroing open amount from the grid must fully close the invoice
                    if (x.open_tn != undefined && openLeft <= 0.01) {
                        return {...x, open_tn: 0, status: 'closed' as const}
                    }
                    return x
                })
            await update_all_tn(eligible, sup)
            await cache.invalidateQueries(pool)

            refetch()

            return true
        } catch (e) {
            console.log(e)
            return false
        }

    }, [sup, cache, pool, refetch])

    const [selectedCustomersSend, setSelectedCustomersSend] = React.useState<any>(undefined)
    const [paymentLinkOpen, setPaymentLinkOpen] = React.useState<{
        amount: number;
        invoiceIds: number[];
    } | null>(null);
    return (
        <>
            {(!customer || isMutating || isLoading) && <Loader/>}

            <MainCard
                title={
                    <Box>
                        <CartesetFilterBar range={range} setRange={setRange} cid={cid} debt={debt} loading={isLoading}/>
                    </Box>
                }
            >
                <CartesetLinksOverlay
                    gridRef={gridRef}
                    links={cartesetLinks}
                    rowKey={cartesetRowKey}
                    // Supplier carteset swaps the columns: payments show under חובה.
                    fromColId={sup ? 'value_tn' : 'value_pay'}
                    toColId={sup ? 'value_pay' : 'value_tn'}
                    hidden={!showLinks}
                >
                    <AgTable
                        data={carteset}
                        columns={cols}
                        tableControls={tableControls}
                        cellRenderer={cellRenderer}
                        chipDetails={chipDetails}
                        selectionControls={selectionControls}
                        pinnedControl={basicPinnedControl()}
                        extentedContextMenu={contextMenu}
                        saveDataCallback={saveDataCallback}
                        outSideTableRef={gridRef}
                    />
                </CartesetLinksOverlay>
                {currentDocument ? <Dialog fullWidth open={true} onBackdropClick={() => setCurrentDocument(undefined)}
                                           sx={{'& .MuiDialog-paper': {p: 0}}}>
                    <DocumentPreview setSelectedCustomersSend={setSelectedCustomersSend} document={currentDocument}
                                     pool={pool}
                                     onUpdated={(updated) => setCurrentDocument(updated)}
                                     onCanceled={() => {
                                         void refetch();
                                     }}/>
                </Dialog> : undefined}

                <DialogAddManualDate id={customer!.id} open={addOpen} setOpen={setAddOpen} pool={pool} sup={sup}/>
            </MainCard>
            {selectedCustomersSend != undefined ? <SendMessageDialog
                open={selectedCustomersSend != undefined}
                setOpen={(open) => setSelectedCustomersSend(undefined)}

                toSelected={selectedCustomersSend?.selected ?? []}
                files={selectedCustomersSend?.files ?? []}
                msg={selectedCustomersSend?.msg ?? ""}
                subject={selectedCustomersSend?.subject ?? ""}
                defaultPlatform={selectedCustomersSend?.defaultPlatform ?? "email"}
                withFiles={false}
                externalVar={selectedCustomersSend?.externalVar ?? []}

            /> : undefined}
            {paymentLinkOpen && customer && (
                <PaymentLinkDialog
                    open
                    clientId={Number(cid)}
                    clientName={customer.name ?? customer.nkObject?.name ?? ''}
                    debt={paymentLinkOpen.amount}
                    invoiceIds={paymentLinkOpen.invoiceIds}
                    email={customer.nkObject?.email || customer.email}
                    phone={customer.phone ?? customer.nkObject?.phone}
                    address={[
                        customer.nkObject?.street,
                        customer.nkObject?.streetNumber ?? customer.nkObject?.street_number,
                    ].filter(Boolean).join(' ')}
                    city={customer.city ?? customer.nkObject?.city}
                    onClose={() => setPaymentLinkOpen(null)}
                />
            )}
            {recipt ? <Dialog maxWidth={'md'} fullWidth open={true} disableEscapeKeyDown={true} onBackdropClick={() => {
                const docs = recipt?.documents?.map((d) => d.id)
                if (docs) {
                    const types = recipt?.documents?.map((d) => d.nkObject.getConnectedDocType())
                    refetch()
                    // refresh.mutate({ids: docs, types: types, offline: false})
                }

                setRecipt(undefined)

            }}
            >
                <DocumentNew
                    customerId={recipt?.documents?.[0]?.entId}
                    type={recipt?.type}
                    refId={recipt?.documents?.map((d) => d.id)}
                    types={recipt?.documents?.map((d) => d.type)}
                />
            </Dialog> : undefined}
        </>
    );
};

export default CartesetList;
